import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { TextField } from '@mui/material';

// Components
import { useAutoFocus } from '@Components/shared/hooks/useAutoFocus';
import { usePressEnter } from '@Components/shared/hooks/usePressEnter';
import { useIntegerFormat } from '@Components/shared/hooks/useIntegerFormat';
import { FilterColumnDialogContentProps } from '@Components/props/table/FilterColumnDialogContentProps';
import { TableFilterDialogLayout } from '../TableFilterDialogLayout';

type Props = FilterColumnDialogContentProps<number | undefined>;

export const IntegerTableFilterDialogContent: React.FC<Props> = ({
  open,
  codeName,
  handleSetFilters,
}) => {
  const { correct, validate, format, parse } = useIntegerFormat({ sign: true });

  const [text, setText] = useState<string | null>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    setText('');
    setError('');
  }, [format, open]);

  const ref = useAutoFocus<HTMLDivElement>(open);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setText(correct(event.target.value || ''));
    },
    [setText, correct]
  );

  const handleApplyClick = useCallback(() => {
    const message = 'Value should be number';
    if (!text) {
      handleSetFilters && handleSetFilters({ codeName: codeName, value: undefined });
      return;
    }
    if (!validate(text)) {
      setError(message);
      return;
    }
    const v = parse(text);
    if (!Number.isFinite(v)) {
      setError(message);
      return;
    }
    handleSetFilters && handleSetFilters({ codeName: codeName, value: text });
  }, [text, setError, validate, parse]);

  const handleResetClick = useCallback(() => {
    handleSetFilters && handleSetFilters({ codeName: codeName, value: undefined });
  }, []);

  const handleKeyUp = usePressEnter<HTMLDivElement>(handleApplyClick);

  return (
    <TableFilterDialogLayout
      onApplyClick={handleApplyClick}
      onResetClick={handleResetClick}
      sx={{ width: '193px' }}
    >
      <TextField
        size="small"
        label="Search by"
        data-at-text-field="IntegerFilter"
        value={text}
        onChange={handleChange}
        inputRef={ref}
        onKeyUp={handleKeyUp}
        error={error !== ''}
        helperText={error}
      />
    </TableFilterDialogLayout>
  );
};
